import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Briefcase, Phone, User, MapPin, Calendar, FileText } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Admin · Hire Negotiator Requests dashboard.
 */
export default function AdminHireRequestsPage() {
  const { user, ready } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hire-requests");
      setRequests(r.data.requests || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load requests"));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (ready && user?.role === "admin") load(); }, [ready, user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/hire-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  if (!ready) return <div className="max-w-7xl mx-auto px-4 py-20 text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const counts = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    assigned: requests.filter((r) => r.status === "assigned").length,
    closed: requests.filter((r) => r.status === "closed").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground underline">← Admin Console</Link>
      </div>
      <div className="mt-2 text-xs uppercase tracking-[0.2em] font-bold text-brand">Admin</div>
      <h1 className="t-page flex items-center gap-3">
        <Briefcase size={28} /> Hire Negotiator Requests
      </h1>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: "Total", v: counts.total },
          { l: "Pending", v: counts.pending },
          { l: "Assigned", v: counts.assigned },
          { l: "Closed", v: counts.closed },
        ].map((s) => (
          <div key={s.l} className="border border-border p-4 rounded-none" data-testid={`hire-stat-${s.l.toLowerCase()}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.l}</div>
            <div className="font-heading font-extrabold text-2xl">{s.v}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading requests…</p>
      ) : requests.length === 0 ? (
        <div className="mt-8 border border-dashed border-border py-16 text-center" data-testid="hire-empty">
          <Briefcase size={32} className="mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-3">No hire-negotiator requests yet.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {requests.map((r) => (
            <div key={r.id} className="border border-border rounded-none p-5 bg-card" data-testid={`hire-request-${r.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    {r.product_snapshot?.category_label || r.product_snapshot?.category}
                  </div>
                  <h3 className="font-heading font-bold text-xl mt-1">{r.product_snapshot?.name}</h3>
                  <div className="text-sm text-muted-foreground">
                    {r.product_snapshot?.brand} · {r.product_snapshot?.typology}
                  </div>
                </div>
                <span
                  className={`bhega-pill ${
                    r.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                    r.status === "assigned" ? "bg-[var(--bhega-info-soft)] text-[var(--bhega-info)]" :
                    "bg-raised text-[var(--bhega-text)]"
                  }`}
                  data-testid={`hire-status-${r.id}`}
                >
                  {r.status}
                </span>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div className="border-l-2 border-[#FFBE06] pl-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><User size={11} /> Buyer</div>
                  <div className="font-semibold">{r.user_name || "Anonymous"}</div>
                </div>
                <div className="border-l-2 border-[#FFBE06] pl-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Phone size={11} /> Phone</div>
                  <a href={`tel:+91${r.user_phone}`} className="font-semibold hover:underline" data-testid={`hire-phone-${r.id}`}>+91 {r.user_phone}</a>
                </div>
                <div className="border-l-2 border-[#FFBE06] pl-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Intended buyers</div>
                  <div className="font-heading font-extrabold text-lg">{r.intended_buyers}</div>
                </div>
                <div className="border-l-2 border-[#FFBE06] pl-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MapPin size={11} /> City</div>
                  <div className="font-semibold">{r.city || "—"}</div>
                </div>
              </div>

              {r.notes && (
                <div className="mt-3 text-sm bg-foreground/5 border-l-4 border-[#FFBE06] p-3 rounded-none">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><FileText size={11} /> Notes</div>
                  <p className="mt-1">{r.notes}</p>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar size={11} /> Submitted {new Date(r.created_at).toLocaleString("en-IN")}
                </div>
                <div className="flex gap-2">
                  {r.status !== "assigned" && (
                    <button onClick={() => updateStatus(r.id, "assigned")} className="btn-outline text-xs" data-testid={`hire-mark-assigned-${r.id}`}>
                      Mark Assigned
                    </button>
                  )}
                  {r.status !== "closed" && (
                    <button onClick={() => updateStatus(r.id, "closed")} className="btn-secondary text-xs" data-testid={`hire-mark-closed-${r.id}`}>
                      Close request
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
